import React, { useEffect, useState } from "react";
import { apiClient } from "../../shared/customHooks/hooks/useApiClient";
import * as Yup from "yup";
import { useNavigate } from "react-router";
import { FormikProps, FormikProvider, useFormik } from "formik";
import { AxiosResponse } from "axios";
import { Button } from "primereact/button";
import { FormGroup } from "./models/models";
import { FormContext } from "./context/formContext";
import { ToastInfo } from "../toaster/toaster";
import { eventEmitter } from "../../shared/event";
import { getInitalValuesAndValidationSchema } from "./components/formCreator/helpers";
import FormCreatorComponent from "./components/formCreator/FormCreatorComponent";
import LeavePageWarning from "./components/leavePage/LeavePageWarning";

interface FormProps {
  id?: string | null;
  apiClient: apiClient;
  data?: any;
  formBody: FormGroup;
  formContext?: any;
  formTitle?: string;
  submitRoute?: string | null;
  saveSuccessCB?: (data?: any) => void;
  saveErrorCB?: (error?: any) => void;
  getInitialValues?: (data: any, formik: FormikProps<any>) => void;
  customSubmit?: (data: any, isSubmitting: (isSubmitting: boolean) => void, isValid: boolean) => void;
  disabled?: boolean;
  cancelPath?: string;
  showInternalNotes?: boolean;
  submitBtnTitle?: string;
  noValidateBtnTitle?: string;
  showNoValidateBtn?: boolean;
  hidden?: boolean;
  backPath?: string;
  submitApiPath?: string;
  hideCancelButton?: boolean;
  hideSubmitButton?: boolean;
  doFetch?: boolean;
  displayEmptyForm?: boolean;
}

const FormContainer: React.FC<FormProps> = ({
  id,
  data,
  apiClient,
  formBody,
  formContext,
  submitRoute,
  saveSuccessCB,
  saveErrorCB,
  getInitialValues,
  customSubmit,
  formTitle,
  disabled = false,
  cancelPath,
  showInternalNotes = true,
  submitBtnTitle = "Save",
  noValidateBtnTitle = "Save",
  showNoValidateBtn = false,
  hidden = false,
  backPath,
  submitApiPath,
  hideCancelButton,
  hideSubmitButton,
  doFetch = true,
  displayEmptyForm = false,
}) => {
  const [validation, setValidation] = useState(Yup.object().shape({}));
  const [initForm, setInitForm] = useState(false);
  const [showFormDetails, setShowFormDetails] = useState(false);
  const [newForm, setNewForm] = useState(true);
  const [formContextStore, setFormContextStore] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (!formBody.fields.some((field) => field.key === "internalNotes") && showInternalNotes) {
      formBody.fields.push({
        type: "field",
        key: "internalNotes",
        fieldType: "textarea",
        label: "Internal notes",
      });
    }
    const [model, yup] = getInitalValuesAndValidationSchema(formBody);

    formik.setValues(model);
    setValidation(yup);
    if (data != null) {
      formik.setValues({ ...model, ...data });
      getInitialValues?.(data, formik);
      setInitForm(true);
    } else if (id && doFetch) {
      apiClient
        .fetchData(id)
        .then((res) => {
          formik.setValues({ ...model, ...(res.data ?? {}) });
          data = { ...(res.data ?? {}) };
          setNewForm(false);
          getInitialValues?.(res.data, formik);
          resetShowFormDetails();
        })
        .catch(() => {
          getInitialValues?.(model, formik);
        })
        .finally(() => {
          setInitForm(true);
        });
    } else {
      setInitForm(true);
      getInitialValues?.(model, formik);
    }
    resetShowFormDetails();
  }, [id]);

  function resetShowFormDetails() {
    setShowFormDetails(!disabled || (disabled && ((data != null && JSON.stringify(data) != "{}") || displayEmptyForm)));
  }
  useEffect(() => {
    setFormContextStore({ disabled, ...formContext });
  }, [formContext]);

  const formik = useFormik({
    initialValues: {},
    validationSchema: validation,
    onSubmit: async (values, { setSubmitting }) => {
      if (customSubmit != null) {
        customSubmit(values, setSubmitting, formik.isValid);
        return;
      }
      if (!formik.isValid) {
        setSubmitting(false);
        return;
      }
      saveSubmit(values, true);
    },
  });

  async function saveSubmit(values: any, isSubmit: boolean) {
    const saveType = newForm ? "Submission" : "Update";
    const copy = clearHiddenFields(values); // clear hidden values
    try {
      const res: AxiosResponse<any> = newForm
        ? await apiClient.postData(copy, submitApiPath == null ? (id ?? undefined) : submitApiPath, { isSubmit })
        : await apiClient.putData(copy, submitApiPath == null ? id! : submitApiPath, { isSubmit });
      saveSuccessCB?.(res.data);
      setNewForm(false);
      const successData: ToastInfo = { title: "Success", message: saveType + " Successful!", severity: "success" };
      eventEmitter.emit("tstr", successData);
      eventEmitter.emit("IsDirtyForm", false);
      if (submitRoute) {
        navigate(submitRoute);
      }
    } catch (error: any) {
      saveErrorCB?.(error);
      if (error.response.data.validationResults) {
        error.response.data.validationResults.forEach((err: any) => {
          const errorData: ToastInfo = { title: "Error", message: err.errorMessage, severity: "error" };
          eventEmitter.emit("tstr", errorData);
        });
      } else if (error.response.data.message) {
        const errorData: ToastInfo = { title: "Error", message: error.response.data.message, severity: "error" };
        eventEmitter.emit("tstr", errorData);
      } else if (error.status == 400) {
        const errorData: ToastInfo = { title: "Error", message: error.response.data, severity: "error" };
        eventEmitter.emit("tstr", errorData);
      }
    } finally {
      formik.setSubmitting(false);
    }
  }

  async function noValidateSave() {
    await saveSubmit(formik.values, false);
  }

  useEffect(() => {
    const firstError = document.getElementsByClassName("error")[0] as HTMLElement;
    firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [formik?.isSubmitting]);

  async function checkAndScollErrors() {
    await formik.validateForm();
  }

  function clearHiddenFields(values: any) {
    if (Array.isArray(values)) {
      let copy = [...values];
      copy = copy.map((val) => clearHiddenFields(val));
      return copy;
    } else if (typeof values === "object" && values !== null && !(values instanceof Date)) {
      const copy = { ...values };
      Object.keys(copy).forEach((key) => {
        if (copy[key + "Hidden"]) {
          delete copy[key];
        } else {
          copy[key] = clearHiddenFields(copy[key]);
        }
      });
      return copy;
    } else {
      return values;
    }
  }

  const [customTouched, setCustomTouched] = useState(false);
  useEffect(() => {
    if (!customTouched && Object.keys(formik.touched).length > 0) {
      setCustomTouched(Object.keys(formik.touched).length > 0);
      eventEmitter.emit("IsDirtyForm", Object.keys(formik.touched).length > 0);
    }
  }, [formik.touched]);

  return (
    <>
      {showFormDetails && (
        <div hidden={hidden} className="w-full">
          <div className="text-left home-text">
            <form className="" onSubmit={formik.handleSubmit} key="form">
              {formTitle && <h1>{formTitle}</h1>}
              <div>
                {initForm && (
                  <FormikProvider value={formik}>
                    <FormContext.Provider value={{ formContextStore, setFormContextStore, validation, setValidation }}>
                      {!disabled && (
                        <LeavePageWarning path={backPath ?? ".."} isFormDirty={JSON.stringify(formik.touched) !== "{}"}></LeavePageWarning>
                      )}
                      <FormCreatorComponent formBody={formBody} />
                    </FormContext.Provider>
                  </FormikProvider>
                )}
                {!disabled && (
                  <>
                    <div className="form-container-padding">
                      {showNoValidateBtn && (
                        <Button
                          className="save-button  mr-3"
                          type="button"
                          disabled={formik.isSubmitting}
                          label={noValidateBtnTitle}
                          onClick={() => {
                            noValidateSave();
                          }}
                        />
                      )}
                      {!hideSubmitButton && (
                        <Button
                          className={newForm ? "submit-button" : "save-button"}
                          type="submit"
                          disabled={formik.isSubmitting}
                          label={submitBtnTitle}
                          onClick={() => {
                            formik.setFieldValue("actionType", "submit");
                            checkAndScollErrors();
                          }}
                        />
                      )}
                      {!hideCancelButton && (
                        <Button
                          type="button"
                          className="cancel-button ml-3"
                          onClick={() => {
                            eventEmitter.emit("IsDirtyForm", false);
                            if (cancelPath) {
                              if (cancelPath?.includes("tab")) {
                                eventEmitter.emit("navigateTab", cancelPath);
                              } else {
                                navigate(cancelPath);
                              }
                            } else navigate(-1);
                          }}
                          label="Cancel"
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default FormContainer;
